
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Search, Filter, CheckSquare, Square } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  assignCourseToUserByStaff,
  unassignCourseFromUserByStaff,
  getUserCourseAssignmentsByStaff
} from '@/hooks/auth/staffUserManagement';
import { getAllAvailableCourses } from '@/hooks/auth/enhancedCourseService';
import EnhancedCourseSearch from '@/components/course/EnhancedCourseSearch';
import { toast } from '@/components/ui/sonner';

interface StaffCourseAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  onAssignmentComplete: () => void;
}

const StaffCourseAssignmentModal: React.FC<StaffCourseAssignmentModalProps> = ({
  isOpen,
  onClose,
  userId,
  userName,
  onAssignmentComplete
}) => {
  const [userAssignments, setUserAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingCourses, setProcessingCourses] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const [selectedCourseIds, setSelectedCourseIds] = useState<Set<string>>(new Set());
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [availableCourses, setAvailableCourses] = useState<any[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(true);

  useEffect(() => {
    if (isOpen && userId) {
      loadUserAssignments();
      loadAvailableCourses();
    }
  }, [isOpen, userId]);

  const loadAvailableCourses = async () => {
    setLoadingCourses(true);
    try {
      console.log('📚 Loading courses from database...');
      const courses = await getAllAvailableCourses();
      console.log(`✅ Loaded ${courses.length} courses`);
      setAvailableCourses(courses);
    } catch (error) {
      console.error('❌ Error loading courses:', error);
      toast.error('Failed to load available courses');
      setAvailableCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  const loadUserAssignments = async () => {
    setLoading(true);
    try {
      const assignments = await getUserCourseAssignmentsByStaff(userId);
      setUserAssignments(assignments);
    } catch (error) {
      console.error('Error loading assignments:', error);
      toast.error('Failed to load user assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCourse = async (courseId: string, courseTitle: string) => {
    setProcessingCourses(prev => new Set(prev).add(courseId));
    
    try {
      const success = await assignCourseToUserByStaff(userId, courseId, 'manual');
      
      if (success) {
        await loadUserAssignments();
        onAssignmentComplete();
      }
    } catch (error) {
      console.error('Error assigning course:', error);
    } finally {
      setProcessingCourses(prev => {
        const newSet = new Set(prev);
        newSet.delete(courseId);
        return newSet;
      });
    }
  };

  const handleUnassignCourse = async (courseId: string) => {
    setProcessingCourses(prev => new Set(prev).add(courseId));
    
    try {
      const success = await unassignCourseFromUserByStaff(userId, courseId);
      
      if (success) {
        await loadUserAssignments();
        onAssignmentComplete();
      }
    } catch (error) {
      console.error('Error unassigning course:', error);
    } finally {
      setProcessingCourses(prev => {
        const newSet = new Set(prev);
        newSet.delete(courseId);
        return newSet;
      });
    }
  };

  const handleCourseToggle = (courseId: string) => {
    setSelectedCourseIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(courseId)) {
        newSet.delete(courseId);
      } else {
        newSet.add(courseId);
      }
      return newSet;
    });
  };

  const handleSelectAllVisible = () => {
    const unassignedCourses = filteredCourses.filter(course => !isAssigned(course.id));
    setSelectedCourseIds(new Set(unassignedCourses.map(c => c.id)));
  };

  const handleDeselectAll = () => {
    setSelectedCourseIds(new Set());
  };

  const handleBulkAssign = async () => {
    if (selectedCourseIds.size === 0) return;

    setIsBulkAssigning(true);
    const coursesToAssign = Array.from(selectedCourseIds);
    let successCount = 0;
    let failCount = 0;

    try {
      // Process all assignments in parallel
      const results = await Promise.allSettled(
        coursesToAssign.map(courseId => 
          assignCourseToUserByStaff(userId, courseId, 'manual')
        )
      );

      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          successCount++;
        } else {
          failCount++;
        }
      });

      // Clear selection and reload
      setSelectedCourseIds(new Set());
      await loadUserAssignments();
      onAssignmentComplete();

      // Show summary toast
      if (successCount > 0) {
        toast.success(`Successfully assigned ${successCount} course${successCount !== 1 ? 's' : ''} to ${userName}`);
      }
      if (failCount > 0) {
        toast.error(`Failed to assign ${failCount} course${failCount !== 1 ? 's' : ''}`);
      }
    } catch (error) {
      console.error('Error in bulk assignment:', error);
      toast.error('An error occurred during bulk assignment');
    } finally {
      setIsBulkAssigning(false);
    }
  };

  const isAssigned = (courseId: string) => {
    return userAssignments.some(assignment => assignment.course_id === courseId);
  };

  const getAssignmentStatus = (courseId: string) => {
    const assignment = userAssignments.find(a => a.course_id === courseId);
    return assignment?.status || 'not_assigned';
  };

  const getCourseTypeColor = (type: string) => {
    const colors = {
      react: 'bg-blue-100 text-blue-800',
      python: 'bg-green-100 text-green-800',
      sql: 'bg-purple-100 text-purple-800',
      ai: 'bg-orange-100 text-orange-800',
      java: 'bg-red-100 text-red-800',
      javascript: 'bg-yellow-100 text-yellow-800',
      aws: 'bg-indigo-100 text-indigo-800',
      dotnet: 'bg-pink-100 text-pink-800',
      azure: 'bg-cyan-100 text-cyan-800',
      devops: 'bg-emerald-100 text-emerald-800',
      backend: 'bg-slate-100 text-slate-800',
      security: 'bg-red-100 text-red-800',
      mobile: 'bg-violet-100 text-violet-800',
      gcp: 'bg-amber-100 text-amber-800',
      database: 'bg-teal-100 text-teal-800',
      blockchain: 'bg-purple-100 text-purple-800',
      rpa: 'bg-orange-100 text-orange-800',
      graphics: 'bg-rose-100 text-rose-800',
      enterprise: 'bg-gray-100 text-gray-800'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      paused: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  // Filter courses based on search and filters
  const filteredCourses = availableCourses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || course.type === filterType;
    const matchesLevel = filterLevel === 'all' || course.level.toLowerCase() === filterLevel.toLowerCase();
    
    return matchesSearch && matchesType && matchesLevel;
  });

  // Get unique course types and levels for filters (filter out undefined)
  const courseTypes = [...new Set(availableCourses.map(c => c.type).filter(Boolean))];
  const courseLevels = [...new Set(availableCourses.map(c => c.level).filter(Boolean))];

  // Stats for summary
  const unassignedFilteredCourses = filteredCourses.filter(c => !isAssigned(c.id));
  const selectedCount = selectedCourseIds.size;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Course Assignments for {userName}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
            {/* Current Assignments */}
            <Card className="flex flex-col">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  Current Assignments
                  <Badge variant="outline">{userAssignments.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : userAssignments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No courses assigned yet</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px] pr-4">
                    <div className="space-y-3">
                      {userAssignments.map((assignment) => {
                        const course = availableCourses.find(c => c.id === assignment.course_id);
                        return (
                          <div key={assignment.id} className="border rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-medium">
                                {course?.title || assignment.course_id}
                              </h3>
                              <Badge className={getStatusColor(assignment.status)}>
                                {assignment.status}
                              </Badge>
                            </div>
                            {course && (
                              <div className="flex gap-2 mb-2">
                                <Badge className={getCourseTypeColor(course.type)}>
                                  {course.type}
                                </Badge>
                                <Badge variant="outline">{course.level}</Badge>
                              </div>
                            )}
                            <div className="text-sm text-muted-foreground mb-3">
                              Assigned: {new Date(assignment.assigned_date).toLocaleDateString()}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnassignCourse(assignment.course_id)}
                              disabled={processingCourses.has(assignment.course_id)}
                            >
                              {processingCourses.has(assignment.course_id) ? 'Removing...' : 'Remove'}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Available Courses */}
            <Card className="flex flex-col">
              <CardHeader>
                <CardTitle className="text-lg">Available Courses</CardTitle>
                <div className="space-y-4">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search courses..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                  
                  {/* Filters */}
                  <div className="flex gap-2">
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {courseTypes.map(type => (
                          <SelectItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={filterLevel} onValueChange={setFilterLevel}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Levels</SelectItem>
                        {courseLevels.map(level => (
                          <SelectItem key={level} value={level.toLowerCase()}>
                            {level}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Bulk Action Controls */}
                  {unassignedFilteredCourses.length > 0 && (
                    <>
                      <Separator />
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSelectAllVisible}
                            disabled={isBulkAssigning}
                          >
                            <CheckSquare className="h-4 w-4 mr-2" />
                            Select All Visible
                          </Button>
                          {selectedCount > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleDeselectAll}
                              disabled={isBulkAssigning}
                            >
                              <Square className="h-4 w-4 mr-2" />
                              Deselect All
                            </Button>
                          )}
                        </div>
                        {selectedCount > 0 && (
                          <Button
                            size="sm"
                            onClick={handleBulkAssign}
                            disabled={isBulkAssigning}
                          >
                            {isBulkAssigning ? `Assigning ${selectedCount}...` : `Assign Selected (${selectedCount})`}
                          </Button>
                        )}
                      </div>
                      {selectedCount > 0 && (
                        <div className="text-sm text-muted-foreground">
                          {selectedCount} of {unassignedFilteredCourses.length} courses selected
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden">
                {filteredCourses.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Filter className="h-12 w-12 mx-auto mb-2" />
                    <p>No courses match your search criteria</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px] pr-4">
                    <div className="space-y-3">
                      {filteredCourses.map((course) => {
                        const assigned = isAssigned(course.id);
                        const status = getAssignmentStatus(course.id);
                        const isSelected = selectedCourseIds.has(course.id);
                        
                        return (
                          <div 
                            key={course.id} 
                            className={`border rounded-lg p-4 transition-all cursor-pointer hover:border-primary/50 ${
                              isSelected && !assigned ? 'border-primary bg-primary/5' : ''
                            }`}
                            onClick={() => !assigned && handleCourseToggle(course.id)}
                          >
                            <div className="flex items-start gap-3">
                              {!assigned && (
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => handleCourseToggle(course.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="mt-1"
                                />
                              )}
                              <div className="flex-1">
                                <div className="flex justify-between items-start mb-2">
                                  <h3 className="font-medium">{course.title}</h3>
                                  {assigned && (
                                    <Badge className={getStatusColor(status)}>
                                      {status}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                                  {course.description}
                                </p>
                                <div className="flex gap-2 mb-3">
                                  <Badge className={getCourseTypeColor(course.type)}>
                                    {course.type}
                                  </Badge>
                                  <Badge variant="outline">{course.level}</Badge>
                                  <Badge variant="secondary">{course.totalDuration}</Badge>
                                </div>
                                <div onClick={(e) => e.stopPropagation()}>
                                  {assigned ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleUnassignCourse(course.id)}
                                      disabled={processingCourses.has(course.id)}
                                    >
                                      {processingCourses.has(course.id) ? 'Removing...' : 'Remove Assignment'}
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAssignCourse(course.id, course.title);
                                      }}
                                      disabled={processingCourses.has(course.id)}
                                    >
                                      {processingCourses.has(course.id) ? 'Assigning...' : 'Assign Course'}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {selectedCount > 0 && <span className="font-medium text-primary">{selectedCount} selected • </span>}
            {userAssignments.length} assigned • {unassignedFilteredCourses.length} available
          </div>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StaffCourseAssignmentModal;
